// GraphQL operations for the Cursos area: the project's course list, manual
// course creation, the Course detail screen, per-episode media generation, and
// on-demand presigned media URLs.
//
// Each frontend feature owns its own operations file (see graphql/projects.ts,
// graphql/project-detail.ts, graphql/profiles.ts, graphql/generations.ts). This
// file backs task 16.4 — the course list + manual create in the Cursos tab and
// the Course detail screen with media playback. The agent-generation flow
// (agentGenerateCourse / approveCourseStructure) is owned by task 16.5 and
// lives in its own file, so nothing here touches those documents.
//
// To list a project's courses we query the `courses` field the schema exposes
// on `Project` (see the design's GraphQL Schema / getProject), scoped to the
// project the tab is showing — as a dedicated document so the Cursos tab caches
// and refetches courses independently of the project-detail header query.
//
// Requirements 4.3 (getCourse returns episodes ordered by ascending index),
// 7.1 / 8.1 (owner/editor submit episode audio/video generation), and 9.2
// (presigned media URLs with a 60–604800s validity, default 3600). Design →
// GraphQL Schema (Query.getCourse/getMediaUrl,
// Mutation.createCourse/generateEpisodeAudio/generateEpisodeVideo) and Frontend
// (Course detail).

import { gql } from '@apollo/client';
import type { Course, Episode, GenerationRequest, AwsDateTime } from '@courseai/domain';

// ---------------------------------------------------------------------------
// Course list (Cursos tab) — Project.courses
// ---------------------------------------------------------------------------

/**
 * The Course fields the Cursos list renders: identity, the title shown as the
 * link label, and the status badge. `topicPrompt`/`language` come along so a
 * freshly created course hydrates the cache with the same shape.
 */
export type CourseListItem = Pick<
  Course,
  'projectId' | 'courseId' | 'title' | 'topicPrompt' | 'language' | 'status' | 'createdAt'
>;

/** Variables for the `getProject`-scoped courses query. */
export interface ProjectCoursesVariables {
  readonly projectId: string;
}

/**
 * Shape of the courses query result. `getProject` is nullable server-side (a
 * non-member receives an authorization error and the field resolves to null);
 * the tab treats an absent project as an empty course list.
 */
export interface ProjectCoursesData {
  readonly getProject: {
    readonly projectId: string;
    readonly courses: ReadonlyArray<CourseListItem>;
  } | null;
}

/**
 * List a project's courses for the Cursos tab. Membership is enforced
 * server-side; identity is derived from the JWT, so none is sent. This is a
 * distinct document from the project-detail header query so the tab can cache
 * and refetch courses independently.
 */
export const GET_PROJECT_COURSES = gql`
  query GetProjectCourses($projectId: ID!) {
    getProject(projectId: $projectId) {
      projectId
      courses {
        projectId
        courseId
        title
        topicPrompt
        language
        status
        createdAt
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Manual course creation — createCourse
// ---------------------------------------------------------------------------

/** Variables for `createCourse`. Mirrors `CreateCourseInput`. */
export interface CreateCourseVariables {
  readonly input: {
    readonly projectId: string;
    readonly title: string;
    readonly topicPrompt: string;
    readonly language?: string | undefined;
  };
}

/** Shape of the `createCourse` mutation result. */
export interface CreateCourseData {
  readonly createCourse: CourseListItem;
}

/**
 * Create a course within the project. Owner/editor only, enforced server-side
 * (Req 4.1, 4.7). The server validates the title (1–200) and topic prompt
 * (1–2000) and sets the status to draft with the language defaulting to Spanish
 * (Req 4.2, 4.4, 4.5, 4.6, 4.8); the client mirrors the length validation for
 * immediate feedback.
 */
export const CREATE_COURSE = gql`
  mutation CreateCourse($input: CreateCourseInput!) {
    createCourse(input: $input) {
      projectId
      courseId
      title
      topicPrompt
      language
      status
      createdAt
    }
  }
`;

// ---------------------------------------------------------------------------
// Course detail — getCourse (course + ordered episodes)
// ---------------------------------------------------------------------------

/**
 * The Episode fields the Course detail screen renders: index and title, the
 * script presence indicator, proposed duration, the per-kind generation
 * statuses (audio/video), and the stored object keys used to fetch a presigned
 * playback URL on demand.
 */
export type EpisodeDetail = Pick<
  Episode,
  | 'projectId'
  | 'courseId'
  | 'episodeIndex'
  | 'title'
  | 'script'
  | 'proposedDurationSeconds'
  | 'audioObjectKey'
  | 'videoObjectKey'
  | 'audioStatus'
  | 'videoStatus'
>;

/**
 * The Course fields the detail screen renders: identity, header (title, topic,
 * language, status) and the ordered episode list. Episodes arrive ordered by
 * ascending `episodeIndex` (Req 4.3).
 */
export type CourseDetail = Pick<
  Course,
  'projectId' | 'courseId' | 'title' | 'topicPrompt' | 'language' | 'status' | 'createdAt'
> & {
  readonly episodes: ReadonlyArray<EpisodeDetail>;
};

/** Variables for the `getCourse` query. */
export interface GetCourseVariables {
  readonly projectId: string;
  readonly courseId: string;
}

/** Shape of the `getCourse` query result. `getCourse` is nullable server-side. */
export interface GetCourseData {
  readonly getCourse: CourseDetail | null;
}

/**
 * Load a course and its episodes for the Course detail screen. The API returns
 * episodes ordered by ascending `episodeIndex` (Req 4.3). Membership is
 * enforced server-side; identity is derived from the JWT, so none is sent.
 */
export const GET_COURSE = gql`
  query GetCourse($projectId: ID!, $courseId: ID!) {
    getCourse(projectId: $projectId, courseId: $courseId) {
      projectId
      courseId
      title
      topicPrompt
      language
      status
      createdAt
      episodes {
        projectId
        courseId
        episodeIndex
        title
        script
        proposedDurationSeconds
        audioObjectKey
        videoObjectKey
        audioStatus
        videoStatus
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Per-episode media generation — generateEpisodeAudio / generateEpisodeVideo
// ---------------------------------------------------------------------------

/**
 * The GenerationRequest fields returned by the episode generation mutations.
 * The detail screen only needs to confirm the request was accepted (its id and
 * initial PENDING status); the Generaciones tab tracks live status thereafter
 * via its subscription (Req 6.1, 6.6).
 */
export type GenerationRequestAck = Pick<
  GenerationRequest,
  'projectId' | 'generationRequestId' | 'kind' | 'status' | 'courseId' | 'episodeIndex'
>;

/** Variables for `generateEpisodeAudio`. Mirrors `GenerateEpisodeAudioInput`. */
export interface GenerateEpisodeAudioVariables {
  readonly input: {
    readonly projectId: string;
    readonly courseId: string;
    readonly episodeIndex: number;
    readonly voiceProfileId?: string | undefined;
  };
}

/** Shape of the `generateEpisodeAudio` mutation result. */
export interface GenerateEpisodeAudioData {
  readonly generateEpisodeAudio: GenerationRequestAck;
}

/**
 * Submit an episode audio-generation operation. Owner/editor only, enforced
 * server-side (Req 7.1). The server rejects the request when the project has no
 * Voice_Profile or the episode has no script text (Req 7.5, 7.6). The
 * background job runs asynchronously; the returned GenerationRequest starts
 * PENDING and its progress is surfaced by the Generaciones tab.
 */
export const GENERATE_EPISODE_AUDIO = gql`
  mutation GenerateEpisodeAudio($input: GenerateEpisodeAudioInput!) {
    generateEpisodeAudio(input: $input) {
      projectId
      generationRequestId
      kind
      status
      courseId
      episodeIndex
    }
  }
`;

/** Variables for `generateEpisodeVideo`. Mirrors `GenerateEpisodeVideoInput`. */
export interface GenerateEpisodeVideoVariables {
  readonly input: {
    readonly projectId: string;
    readonly courseId: string;
    readonly episodeIndex: number;
    readonly artStyleProfileId?: string | undefined;
    readonly narrationOverlay?: boolean | undefined;
  };
}

/** Shape of the `generateEpisodeVideo` mutation result. */
export interface GenerateEpisodeVideoData {
  readonly generateEpisodeVideo: GenerationRequestAck;
}

/**
 * Submit an episode video-generation operation. Owner/editor only, enforced
 * server-side (Req 8.1, 8.2). The server rejects the request when the project
 * has no Art_Style_Profile, or when narration overlay is requested but no
 * narration audio exists (Req 8.5, 8.7). The background job runs
 * asynchronously; the returned GenerationRequest starts PENDING and its
 * progress is surfaced by the Generaciones tab.
 */
export const GENERATE_EPISODE_VIDEO = gql`
  mutation GenerateEpisodeVideo($input: GenerateEpisodeVideoInput!) {
    generateEpisodeVideo(input: $input) {
      projectId
      generationRequestId
      kind
      status
      courseId
      episodeIndex
    }
  }
`;

// ---------------------------------------------------------------------------
// On-demand media playback — getMediaUrl (presigned URL)
// ---------------------------------------------------------------------------

/**
 * The MediaUrl result (GraphQL `type MediaUrl { url expiresAt }`). The domain
 * package models entities but not this transient query result, so the shape is
 * declared here: the time-limited URL to source an <audio>/<video> element from
 * and the instant it stops working (Req 9.2, 9.5).
 */
export interface MediaUrlResult {
  readonly url: string;
  readonly expiresAt: AwsDateTime;
}

/** Variables for the `getMediaUrl` query. Mirrors `GetMediaUrlInput`. */
export interface GetMediaUrlVariables {
  readonly input: {
    readonly projectId: string;
    readonly objectKey: string;
    /** Validity in seconds, [60, 604800], defaulting to 3600 when omitted. */
    readonly expiresInSeconds?: number | undefined;
  };
}

/** Shape of the `getMediaUrl` query result. */
export interface GetMediaUrlData {
  readonly getMediaUrl: MediaUrlResult;
}

/**
 * Request a presigned read URL for a stored media object. Membership is
 * enforced server-side; a non-member is denied and a missing object yields a
 * not-found error (Req 9.3, 9.4). The URL grants read access for a configurable
 * validity between 60 and 604800 seconds, defaulting to 3600 (Req 9.2). Fetched
 * on demand (playback time) so a fresh, unexpired URL sources the media element.
 */
export const GET_MEDIA_URL = gql`
  query GetMediaUrl($input: GetMediaUrlInput!) {
    getMediaUrl(input: $input) {
      url
      expiresAt
    }
  }
`;
